import * as THREE from 'three';
import type { Track } from '@/world/Track';
import type { PlacedShortcut } from '@/physics/shortcuts';
import { tuning } from '@/config/tuning';
import {
  BoxKit,
  art,
  box,
  canvasTex,
  chStrip,
  dressExit,
  fenceRun,
  glowStrips,
  kickerSkirt,
  mat,
  nodeAt,
  reflectorStuds,
  trenchTexture,
  type ChannelTrack,
} from '@/world/scenery/shortcutMeshes';

/**
 * shortcutTokyo.ts — the ABANDONED EXPRESSWAY SPUR (Tokyo's batch-2 secret
 * shortcut, `look: 'expressway'`). A closed elevated expressway ramp,
 * reclaimed as your secret line: a barricaded on-ramp stub (the NYC
 * works-zone doctrine — looks like an obstacle, operates as a ramp) lofts you
 * onto a derelict viaduct that rides the Shibuya block crest, lifts onto
 * concrete T-piers as the street plunges 18.5 u toward Asakusa, cantilevers
 * back beside the road, and chutes down at 37%.
 *
 * DESIGN LANGUAGE (all from Tokyo's theme in cities.ts): rail glow = the
 * road's own hot pink (0xff2d7a — the deck literally reads as "your line"
 * through the night skyline); the launcher's boost strip keeps the series'
 * cool cyan cue; signage = Japanese expressway green boards (one lit, one
 * dead — the spur is half-abandoned); two vertical neon billboards in the
 * city's pink/cyan face the road below; sodium droop lamps, half the circuit
 * out. Roadworks dressing is the Japanese kit: yellow/black tiger barricades,
 * a 工事中 board, amber blinkers frozen mid-blink, cones.
 *
 * CONTRACT (the M47 rule): everything is built FROM the physics table —
 * `sc.channel` (deck floor + rail clamps), `sc.kicker` (the real launcher
 * incline), `sc.exitDoor` (the road-grade opening). Props flanking the lane
 * sit OUTSIDE channel halfWidth + 0.5; overhead gantry boards clear ≥ 6.2 u
 * over the deck floor (the chase cam rides ~4.8 above the car — measured
 * M116); support piers copy deckStructure's cantilever skip (never a column
 * where the spine sits at |lateral| < road halfWidth + 1.6).
 *
 * MEASURED GOTCHAS (this build):
 *  - Pier projection is HINT-LESS: the spine bows to |lateral| 106 and spans
 *    467 course-arc units, far past the +60-node hint window — a hinted
 *    project() clamps and misreads lateral exactly where the cantilever skip
 *    matters (the M99 "never hint far objects" rule).
 *  - The girder fascia's bottom clamps to max(ground − 0.35, floor − 1.55):
 *    over the block crest (2.5–3.9 u clearance) a fixed-depth girder dug
 *    into the hill; the clamp beds it like an embankment wall and the same
 *    expression opens into the full box girder where the deck flies.
 *  - Fascia AND soffit share one girderBot(nd) sampled at the SPINE — a
 *    per-edge ground sample left seams between their bottoms on the crest.
 *  - The boost strip keeps the M117 incline-decal convention
 *    (−π/2 − atan(slope)); the negated form buries it inside the slab.
 *  - The slab itself carries NO emissive (the M117 wash lesson — a flat tint
 *    with no map turns the whole ramp into a glowing wedge readable from up
 *    the road, the opposite of "looks like roadworks").
 */

// ---- Tokyo palette (from the city theme) ----------------------------------

const PINK = 0xff2d7a; // theme.roadLine — the deck's edge-glow ribbon
const CYAN_CSS = '#00e5ff'; // theme.wall — one billboard + accents
const PINK_CSS = '#ff2d7a';
const BOOST_CYAN = 0x59c8ff; // the series' launcher strip cue (NYC/Lagos match)
const AMBER = 0xffa02a;
const GIRDER_D = 1.55; // box-girder depth below the deck floor
const RAIL_TOP = 1.14; // pipe rail crown over the deck floor

// ---- Canvas painters (deterministic art() hashes, never any RNG) ----------

/** Worn expressway asphalt: faded centre dashes, hatched shoulders, patch
 *  repairs. Canvas x = along the deck (u repeats), y = ACROSS (v 0..1).
 *  MEASURED: the first pass's stronger mottle/patches fused per 6-u tile at
 *  grazing angles into periodic CROSS-BANDS marching down the deck (mipmap
 *  averaging — proven by a map-null probe rendering clean). Low-contrast
 *  grain + anisotropy on the texture is the fix, not geometry. */
function asphaltTexture(): THREE.CanvasTexture {
  const tex = canvasTex(128, 64, (x) => {
    x.fillStyle = '#232630';
    x.fillRect(0, 0, 128, 64);
    for (let i = 0; i < 22; i++) {
      const g2 = 48 + art(i) * 22;
      x.fillStyle = `rgba(${g2},${g2 + 3},${g2 + 12},${0.06 + art(i * 7) * 0.08})`;
      x.fillRect(art(i * 11) * 128, art(i * 13) * 64, 6 + art(i * 17) * 24, 4 + art(i * 19) * 11);
    }
    for (let i = 0; i < 4; i++) {
      x.fillStyle = `rgba(12,13,18,${0.14 + art(i * 23) * 0.12})`;
      x.fillRect(art(i * 29) * 128, 12 + art(i * 31) * 40, 10 + art(i * 37) * 26, 6 + art(i * 41) * 10);
    }
    x.strokeStyle = 'rgba(10,11,15,0.3)';
    x.lineWidth = 1;
    for (let i = 0; i < 7; i++) {
      x.beginPath();
      x.moveTo(art(i * 43) * 128, art(i * 47) * 64);
      x.lineTo(art(i * 43) * 128 + (art(i * 53) - 0.5) * 40, art(i * 47) * 64 + (art(i * 59) - 0.5) * 30);
      x.stroke();
    }
    // Hatched shoulder paint both edges, worn thin.
    x.strokeStyle = 'rgba(206,210,218,0.2)';
    x.lineWidth = 3;
    for (const [y0, y1] of [
      [1, 8],
      [56, 63],
    ] as const) {
      for (let gx = -8; gx < 136; gx += 14) {
        x.beginPath();
        x.moveTo(gx, y1);
        x.lineTo(gx + 7, y0);
        x.stroke();
      }
    }
    x.fillStyle = 'rgba(210,214,222,0.4)';
    x.fillRect(0, 10, 128, 2.5);
    x.fillRect(0, 52, 128, 2.5);
    // Faded centre dashes, chipped by patches of base colour.
    x.fillStyle = 'rgba(214,218,226,0.42)';
    x.fillRect(8, 30, 34, 4);
    x.fillRect(72, 30, 34, 4);
    x.fillStyle = '#232630';
    for (let i = 0; i < 8; i++) {
      x.fillRect(8 + art(i * 61) * 98, 30, 2 + art(i * 67) * 4, 4);
    }
  });
  tex.anisotropy = 8; // keeps the lane paint crisp at chase-cam grazing angles
  return tex;
}

/** Concrete band (parapet / girder fascia): formwork joints + drip stains
 *  running down from the top edge (canvas top = strip TOP; flipY). */
function concreteTexture(base: string, stains: number, jointEvery = 32): THREE.CanvasTexture {
  return canvasTex(128, 32, (x) => {
    x.fillStyle = base;
    x.fillRect(0, 0, 128, 32);
    for (let i = 0; i < 14; i++) {
      const g2 = 120 + art(i * 3) * 40;
      x.fillStyle = `rgba(${g2},${g2 + 2},${g2 + 8},${0.08 + art(i * 5) * 0.1})`;
      x.fillRect(art(i * 7) * 128, art(i * 9) * 32, 8 + art(i * 11) * 20, 4 + art(i * 13) * 10);
    }
    x.strokeStyle = 'rgba(26,28,34,0.4)';
    x.lineWidth = 1;
    for (let gx = 0; gx <= 128; gx += jointEvery) {
      x.beginPath();
      x.moveTo(gx + 0.5, 0);
      x.lineTo(gx + 0.5, 32);
      x.stroke();
    }
    for (let i = 0; i < stains; i++) {
      const sx = art(i * 17) * 128;
      const h = 8 + art(i * 19) * 22;
      x.fillStyle = `rgba(16,18,24,${0.12 + art(i * 23) * 0.14})`;
      x.fillRect(sx, 0, 1.5 + art(i * 29) * 2.5, h);
    }
    x.fillStyle = 'rgba(14,15,20,0.28)';
    x.fillRect(0, 28, 128, 4);
  });
}

/** The launcher slab: worn concrete, construction joints, tyre scuffs,
 *  chevron ghosts nearly weathered off, hazard paint down both edges. */
function rampSlabTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    // Dark site concrete: the first pass's mid-grey washed the boost strip
    // into a broad pale runner (measured from the approach).
    x.fillStyle = '#45474f';
    x.fillRect(0, 0, 64, 64);
    for (let i = 0; i < 18; i++) {
      const g2 = 88 + art(i) * 30;
      x.fillStyle = `rgba(${g2},${g2 + 2},${g2 + 8},${0.1 + art(i * 7) * 0.14})`;
      x.fillRect(art(i * 11) * 64, art(i * 13) * 64, 5 + art(i * 17) * 14, 3 + art(i * 19) * 9);
    }
    x.strokeStyle = 'rgba(30,32,38,0.5)';
    x.lineWidth = 1;
    for (let gy = 16; gy < 64; gy += 16) {
      x.beginPath();
      x.moveTo(0, gy + 0.5);
      x.lineTo(64, gy + 0.5);
      x.stroke();
    }
    // Tyre scuff bands up the climb (canvas y = along the ramp).
    x.fillStyle = 'rgba(18,19,24,0.25)';
    x.fillRect(15, 0, 9, 64);
    x.fillRect(40, 0, 9, 64);
    // Chevron ghosts, nearly gone (the series' entry language, whispering).
    x.globalAlpha = 0.1;
    x.fillStyle = '#d8b93c';
    for (let i = -2; i < 6; i++) {
      x.beginPath();
      x.moveTo(-16 + i * 24, 64);
      x.lineTo(8 + i * 24, 0);
      x.lineTo(20 + i * 24, 0);
      x.lineTo(-4 + i * 24, 64);
      x.closePath();
      x.fill();
    }
    x.globalAlpha = 1;
    // Hazard paint down the edges — in SCRAPS (M117f). Two unbroken bands up
    // both flanks read at speed as runway edge markings, which is the exact
    // "this is a ramp" cue the owner asked us to kill; worn-through segments
    // read as old works paint instead.
    x.globalAlpha = 0.5;
    for (const [ex, y0, len] of [
      [0, 2, 22],
      [0, 38, 15],
      [58, 10, 30],
      [58, 52, 11],
    ] as const) {
      x.fillStyle = '#e3b81f';
      x.fillRect(ex, y0, 6, len);
      x.fillStyle = '#17181c';
      for (let gy = y0 - 8; gy < y0 + len; gy += 12) {
        x.save();
        x.beginPath();
        x.rect(ex, y0, 6, len);
        x.clip();
        x.beginPath();
        x.moveTo(ex, gy + 8);
        x.lineTo(ex + 6, gy);
        x.lineTo(ex + 6, gy + 5);
        x.lineTo(ex, gy + 13);
        x.closePath();
        x.fill();
        x.restore();
      }
    }
    x.globalAlpha = 1;
    // Blown-out patches where the deck has broken through to the base.
    for (let i = 0; i < 5; i++) {
      x.fillStyle = `rgba(26,27,32,${0.4 + art(i * 71) * 0.3})`;
      x.beginPath();
      x.ellipse(8 + art(i * 73) * 48, art(i * 79) * 64, 3 + art(i * 83) * 7, 2 + art(i * 89) * 5, art(i * 97) * 3, 0, Math.PI * 2);
      x.fill();
    }
  });
}

/** Yellow/black tiger-stripe board (the Japanese roadworks barricade). */
function tigerTexture(): THREE.CanvasTexture {
  return canvasTex(64, 32, (x) => {
    x.fillStyle = '#e3b81f';
    x.fillRect(0, 0, 64, 32);
    x.fillStyle = '#17181c';
    for (let i = -1; i < 5; i++) {
      x.beginPath();
      x.moveTo(i * 16, 32);
      x.lineTo(i * 16 + 16, 0);
      x.lineTo(i * 16 + 24, 0);
      x.lineTo(i * 16 + 8, 32);
      x.closePath();
      x.fill();
    }
  });
}

/** 工事中 (under construction) board — black on yellow, black frame. */
function kojiSignTexture(): THREE.CanvasTexture {
  return canvasTex(128, 96, (x) => {
    x.fillStyle = '#e3b81f';
    x.fillRect(0, 0, 128, 96);
    x.strokeStyle = '#17181c';
    x.lineWidth = 7;
    x.strokeRect(5, 5, 118, 86);
    x.fillStyle = '#17181c';
    x.textAlign = 'center';
    x.font = 'bold 34px "Hiragino Sans", sans-serif';
    x.fillText('工事中', 64, 62);
  });
}

/** Expressway gantry board: green with white kanji + romaji + an exit arrow.
 *  `lit` = self-lit sign; dead = ghost text on a dark panel. */
function gantryBoardTexture(lit: boolean): THREE.CanvasTexture {
  return canvasTex(256, 88, (x) => {
    x.fillStyle = lit ? '#0a6e42' : '#141c17';
    x.fillRect(0, 0, 256, 88);
    const ink = lit ? '#f2f7f2' : '#26312a';
    x.strokeStyle = ink;
    x.lineWidth = 3;
    x.strokeRect(5, 5, 246, 78);
    x.fillStyle = ink;
    x.textAlign = 'left';
    x.font = 'bold 34px "Hiragino Sans", sans-serif';
    x.fillText(lit ? '浅草' : '湾岸', 18, 44);
    x.font = '17px "Hiragino Sans", sans-serif';
    x.fillText(lit ? 'Asakusa' : 'Bay Area', 19, 70);
    x.font = 'bold 20px "Hiragino Sans", sans-serif';
    x.fillText('出口', 150, 40);
    x.fillText(lit ? '300m' : '2km', 150, 66);
    x.strokeStyle = ink;
    x.lineWidth = 5;
    x.beginPath();
    x.moveTo(228, 24);
    x.lineTo(228, 56);
    x.moveTo(228, 56);
    x.lineTo(218, 44);
    x.moveTo(228, 56);
    x.lineTo(238, 44);
    x.stroke();
    if (!lit) {
      // Rust bleed down the dead board.
      for (let i = 0; i < 7; i++) {
        x.fillStyle = `rgba(96,58,30,${0.12 + art(i * 31) * 0.14})`;
        x.fillRect(art(i * 37) * 256, 0, 2 + art(i * 41) * 4, 20 + art(i * 43) * 50);
      }
    }
  });
}

/** Vertical neon billboard: stacked katakana glyphs with a tube border glow
 *  (glyph-style ads, no real brands — the city's existing sign language). */
function neonSignTexture(chars: string, color: string): THREE.CanvasTexture {
  return canvasTex(48, 176, (x) => {
    x.fillStyle = '#0b0b14';
    x.fillRect(0, 0, 48, 176);
    x.shadowColor = color;
    x.shadowBlur = 7;
    x.strokeStyle = color;
    x.lineWidth = 2.5;
    x.strokeRect(4, 4, 40, 168);
    x.textAlign = 'center';
    x.font = 'bold 29px "Hiragino Sans", sans-serif';
    x.fillStyle = '#ffffff';
    for (let i = 0; i < chars.length; i++) {
      x.fillText(chars[i], 24, 44 + i * 37);
    }
  });
}

/** Faded white lane-arrow stencil (transparent ground decal). */
function arrowStencilTexture(): THREE.CanvasTexture {
  return canvasTex(32, 96, (x) => {
    x.clearRect(0, 0, 32, 96);
    x.fillStyle = '#dde1e8';
    x.fillRect(12, 26, 8, 66);
    x.beginPath();
    x.moveTo(16, 2);
    x.lineTo(29, 34);
    x.lineTo(3, 34);
    x.closePath();
    x.fill();
    // Worn away in speckles.
    x.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 26; i++) {
      x.fillRect(art(i * 13) * 32, art(i * 17) * 96, 1 + art(i * 19) * 4, 1 + art(i * 23) * 3);
    }
    x.globalCompositeOperation = 'source-over';
  });
}

/** Faded painted speed roundel (the Japanese expressway "40"). */
function speedStencilTexture(): THREE.CanvasTexture {
  return canvasTex(64, 64, (x) => {
    x.clearRect(0, 0, 64, 64);
    x.fillStyle = '#dde1e8';
    x.textAlign = 'center';
    x.font = 'bold 44px sans-serif';
    x.fillText('40', 32, 48);
    x.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 22; i++) {
      x.fillRect(art(i * 29) * 64, art(i * 31) * 64, 1 + art(i * 37) * 4, 1 + art(i * 41) * 3);
    }
    x.globalCompositeOperation = 'source-over';
  });
}

// ---- Small shared builders ------------------------------------------------

/** Compose an instanced-box matrix: position + yaw + (w,h,d) scale. */
function boxMatrix(x: number, y: number, z: number, ry: number, w: number, h: number, d: number): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    new THREE.Vector3(x, y, z),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(0, ry, 0)),
    new THREE.Vector3(w, h, d),
  );
}

function instanced(g: THREE.Group, material: THREE.Material, mats: THREE.Matrix4[]): void {
  if (!mats.length) return;
  const m = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), material, mats.length);
  for (let i = 0; i < mats.length; i++) m.setMatrixAt(i, mats[i]);
  m.instanceMatrix.needsUpdate = true;
  g.add(m);
}

/** A flat additive glow plane (boost strips / under-lip washes). */
function glowPlane(w: number, h: number, color: number, opacity: number): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
}

/** Local slope (rise ahead per unit) of the channel at node i — feeds the
 *  M117 incline-decal convention (−π/2 − atan(slope)). */
function slopeAt(ch: ChannelTrack, i: number): number {
  const a = Math.max(0, i - 1);
  const b = Math.min(ch.nodes.length - 1, i + 1);
  return (ch.nodes[b].y - ch.nodes[a].y) / Math.max(1, (b - a) * ch.step);
}

// ---- The launcher: a barricaded on-ramp stub ------------------------------

function buildRampWorks(track: Track, sc: PlacedShortcut, g: THREE.Group): void {
  const k = sc.kicker;
  if (!k) return;
  const slopeLen = Math.hypot(k.length, k.height);
  const pitch = -Math.atan2(k.height, k.length);
  const fx = Math.sin(k.dir);
  const fz = Math.cos(k.dir);
  const cosP = Math.cos(pitch);
  const cx = k.x + fx * (k.length / 2);
  const cz = k.z + fz * (k.length / 2);

  const rx = Math.cos(k.dir);
  const rz = -Math.sin(k.dir);

  // ---- The stub: SLABS, not a slab (M117f) -------------------------------
  // Owner: "the ramps still look too much like ramps and not enough like
  // roadwork". A single clean wedge is a ramp however it is painted, so the
  // demolished on-ramp is poured in three sections with construction joints
  // that do not line up, at three ages of concrete. Every section's TOP FACE
  // stays in the one launch plane (the yaw is applied about the slope NORMAL),
  // so the drivable surface is byte-identical to the old slab.
  const slabTex = rampSlabTexture();
  const slabMats = [0xffffff, 0xcbc6bd, 0xa79f95].map(
    (tint) => new THREE.MeshStandardMaterial({ map: slabTex, color: tint, roughness: 0.9, metalness: 0.06 }),
  );
  const baseQ = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, k.dir, 0, 'YXZ'));
  const section = (mi: number, latOff: number, halfW: number, t0: number, t1: number, spin: number, lift: number): void => {
    const tm = (t0 + t1) / 2;
    const m = new THREE.Mesh(new THREE.BoxGeometry(halfW * 2, 0.34, (t1 - t0) * slopeLen), slabMats[mi]);
    m.position.set(
      k.x + fx * tm * k.length + rx * latOff,
      k.y + tm * k.height - 0.17 * cosP + lift,
      k.z + fz * tm * k.length + rz * latOff,
    );
    m.quaternion.copy(baseQ).multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), spin));
    g.add(m);
  };
  section(2, -0.6, 2.55, 0.0, 0.58, 0.06, 0);
  section(0, 0.75, 2.5, 0.04, 0.7, -0.07, 0.04);
  section(1, -0.05, 3.15, 0.56, 1.0, 0.025, 0.075);

  // Ballast fill bedding it down (a floating slab read as a sheet, the NYC
  // works-zone lesson) plus the spoil heaped up its flanks — the wedge
  // silhouette is what makes a launcher read as a launcher.
  const fill = new THREE.Mesh(
    new THREE.BoxGeometry(k.halfWidth * 2 - 0.2, 1.6, slopeLen - 0.3),
    mat(0x26272c, 0.98),
  );
  fill.position.set(cx, k.y + k.height / 2 - (0.36 + 0.8) * cosP, cz);
  fill.rotation.order = 'YXZ';
  fill.rotation.y = k.dir;
  fill.rotation.x = pitch;
  g.add(fill);
  const bedMat = mat(0x33353c, 0.99);
  bedMat.side = THREE.DoubleSide;
  bedMat.flatShading = true;
  g.add(kickerSkirt(track, k, bedMat, 2.4, 2.6, 23, sc.door.side));
  // Broken-out lumps of the old deck tipped down the fill — they shade at any
  // sun/neon angle, which a thin berm ribbon does not.
  const rubbleMat = mat(0x4a4d55, 0.97);
  rubbleMat.flatShading = true;
  const rubble = new BoxKit();
  for (let i = 0; i < 14; i++) {
    const t = art(i * 7 + 2) * 1.05 - 0.04;
    const s = i % 2 === 0 ? 1 : -1;
    const lat = s * (k.halfWidth + 0.35 + art(i * 11) * 2.0);
    const px = k.x + fx * t * k.length + rx * lat;
    const pz = k.z + fz * t * k.length + rz * lat;
    const h = 0.32 + art(i * 13) * 0.62;
    rubble.add(
      rubbleMat,
      px,
      track.heightAt(px, pz) + h * 0.42 + Math.min(Math.max(t, 0) * k.height, 1.9) * 0.5,
      pz,
      k.dir + art(i * 17) * 3.1,
      0.5 + art(i * 19) * 1.1,
      h,
      0.5 + art(i * 23) * 1.0,
      (art(i * 29) - 0.5) * 0.5,
    );
  }
  rubble.flush(g);

  // The launch edge: NOT a bar across the top. A scrap of bent steel plate
  // over the middle third, a couple of spalled kerb blocks, and rebar bent
  // out of the broken concrete at the flanks (outside the jumpable width).
  const rustMat = mat(0x6b4a33, 0.85);
  g.add(
    box(k.halfWidth * 0.8, 0.1, 0.34, mat(0x565b61, 0.5), k.x + fx * (k.length - 0.15) + rx * 0.7, k.y + k.height - 0.02, k.z + fz * (k.length - 0.15) + rz * 0.7, k.dir + 0.06, pitch),
  );
  const edgeKit = new BoxKit();
  for (const [t, lat, w] of [
    [0.97, -2.4, 1.3],
    [1.0, 1.9, 0.9],
  ] as const) {
    edgeKit.add(
      slabMats[1],
      k.x + fx * t * k.length + rx * lat,
      k.y + t * k.height - 0.1,
      k.z + fz * t * k.length + rz * lat,
      k.dir + (art(lat * 3) - 0.5) * 0.4,
      w,
      0.3,
      0.5,
    );
  }
  for (let i = 0; i < 6; i++) {
    const s = i % 2 === 0 ? 1 : -1;
    const lat = s * (k.halfWidth + 0.05 + art(i * 3) * 0.35);
    const t = 0.9 + art(i * 5) * 0.14;
    edgeKit.add(
      rustMat,
      k.x + fx * t * k.length + rx * lat,
      k.y + t * k.height + 0.32,
      k.z + fz * t * k.length + rz * lat,
      k.dir + art(i * 9) * 3.1,
      0.07,
      0.9,
      0.07,
      (art(i * 11) - 0.5) * 1.2,
    );
  }
  edgeKit.flush(g);

  // The go-fast cue as ROAD FURNITURE: recessed studs off the centre line,
  // not a lit runway stripe (the old additive strip was the loudest thing on
  // the approach — the M117f cue rule, shared with NYC and Lagos).
  reflectorStuds(g, k, BOOST_CYAN, 6, 0.85);

  // The pavement cut it bridges — flat decal at the foot, pure paint. Uses
  // the shared RAGGED excavation map (alphaTest): the old plain quad left a
  // hard black rectangle on the road, which reads as a placed marking rather
  // than a break-out (measured from the 3/4 angle).
  const cut = new THREE.Mesh(
    new THREE.PlaneGeometry(k.halfWidth * 2 + 3.2, 4.2),
    new THREE.MeshStandardMaterial({ map: trenchTexture(), roughness: 0.98, alphaTest: 0.45 }),
  );
  cut.position.set(k.x - fx * 1.5 + rx * 0.5, k.y + 0.035, k.z - fz * 1.5 + rz * 0.5);
  cut.rotation.order = 'YXZ';
  cut.rotation.y = k.dir;
  cut.rotation.x = -Math.PI / 2;
  g.add(cut);

  // ---- Verge dressing, ALL outside the guardrail (|lateral| > halfWidth) —
  // the run-up lane itself stays clear (the M117 works-zone rule).
  const side = sc.door.side;
  // Hint-less: the kicker is mid-course; a stale hint would clamp (M99).
  const kickArc = track.project(k.x, k.z).distance;
  const at = (arc: number, lat: number): { x: number; y: number; z: number; dir: number } => {
    // Duplicated from shortcutMeshes' works-kicker (not exported).
    const s = track.sampleAt(arc);
    const x = s.x + s.rightX * side * lat;
    const z = s.z + s.rightZ * side * lat;
    return { x, y: track.heightAt(x, z), z, dir: s.dir };
  };

  const tigMat = new THREE.MeshStandardMaterial({ map: tigerTexture(), roughness: 0.85, side: THREE.DoubleSide });
  const legMat = mat(0x2c2f34, 0.8);
  const barricade = (arc: number, lat: number, seed: number): void => {
    const p = at(arc, lat);
    const yaw = p.dir + Math.PI / 2 + (art(seed) - 0.5) * 0.4;
    g.add(box(2.6, 0.5, 0.07, tigMat, p.x, p.y + 0.78, p.z, yaw));
    for (const t of [-1.05, 1.05]) {
      const lx = p.x + Math.sin(p.dir) * t;
      const lz = p.z + Math.cos(p.dir) * t;
      g.add(box(0.09, 1.04, 0.42, legMat, lx, p.y + 0.52, lz, yaw));
    }
  };
  barricade(kickArc - 11, 18.9, 3);
  barricade(kickArc - 2.5, 19.3, 5);
  barricade(kickArc + 6, 18.8, 7);

  // Tiger hoarding panels: TALL enough to read over the course's neon wall
  // band (measured — the 1-u barricades alone vanished behind it from the
  // driver's eye; the works zone must announce itself on approach).
  for (const [arc, len, seed] of [
    [kickArc - 7.5, 5.2, 11],
    [kickArc + 3.5, 4.4, 13],
  ] as const) {
    const p = at(arc, 19.1);
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(len, 1.9), tigMat);
    panel.position.set(p.x, p.y + 1.35, p.z);
    panel.rotation.y = p.dir + Math.PI / 2 + (art(seed) - 0.5) * 0.14;
    g.add(panel);
    for (const t of [-0.46, 0.46]) {
      const q = at(arc + len * t, 19.4);
      g.add(box(0.14, 2.2, 0.14, legMat, q.x, q.y + 1.1, q.z, q.dir));
    }
  }

  // 工事中 board on two posts, faced to the APPROACH (the M116c re-facing
  // lesson: built the other way it renders mirrored). Raised over the wall.
  const sp = at(kickArc + 1.5, 20.3);
  const signMat = new THREE.MeshStandardMaterial({ map: kojiSignTexture(), roughness: 0.8, side: THREE.DoubleSide });
  const board = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 1.28), signMat);
  board.position.set(sp.x, sp.y + 2.0, sp.z);
  board.rotation.y = sp.dir + Math.PI;
  g.add(board);
  for (const t of [-0.7, 0.7]) {
    const lx = sp.x + Math.sin(sp.dir) * t;
    const lz = sp.z + Math.cos(sp.dir) * t;
    g.add(box(0.1, 2.7, 0.1, legMat, lx, sp.y + 1.35, lz, sp.dir));
  }

  // Amber blinkers, frozen mid-blink (one hot, one dim — no real lights),
  // heads clear of the wall band.
  const lampGeo = new THREE.SphereGeometry(0.2, 8, 6);
  for (const [arc, lat, glow] of [
    [kickArc - 6, 19.6, 1.8],
    [kickArc + 10, 19.9, 0.5],
  ] as const) {
    const p = at(arc, lat);
    g.add(box(0.12, 2.5, 0.12, mat(0x3a3d40, 0.7), p.x, p.y + 1.25, p.z, p.dir));
    const lamp = new THREE.Mesh(
      lampGeo,
      new THREE.MeshStandardMaterial({
        color: 0xffb347,
        emissive: new THREE.Color(AMBER),
        emissiveIntensity: glow,
        roughness: 0.4,
      }),
    );
    lamp.position.set(p.x, p.y + 2.55, p.z);
    g.add(lamp);
  }

  // A cone cluster by the works (verge only).
  const coneGeo = new THREE.ConeGeometry(0.34, 0.78, 9);
  const coneMat = mat(0xd85a1c, 0.8);
  const bandGeo = new THREE.CylinderGeometry(0.24, 0.27, 0.13, 9);
  const bandMat = mat(0xe8e9ee, 0.6);
  for (let i = 0; i < 3; i++) {
    const p = at(kickArc + 12.5 + art(i * 3) * 2.4, 19 + art(i * 5) * 1.4);
    const cone = new THREE.Mesh(coneGeo, coneMat);
    cone.position.set(p.x, p.y + 0.39, p.z);
    g.add(cone);
    const band = new THREE.Mesh(bandGeo, bandMat);
    band.position.set(p.x, p.y + 0.42, p.z);
    g.add(band);
  }

  // ---- The compound the stub sits in (M117f) -----------------------------
  // Owner: the launchers must read as ROADWORK. The barricades and the 工事中
  // board announce a closure; what was missing was the yard behind it — the
  // long fenced run you pass, the plant, the stacked material. All of it stays
  // outside the guardrail, batched to a handful of instanced draw calls.
  const kit = new BoxKit();
  const conc = mat(0x767a83, 0.92);
  const steel = mat(0x2c2f34, 0.75);
  const dark = mat(0x1d1f24, 0.9);
  const rust = mat(0x6b4a33, 0.85);
  fenceRun(g, kit, conc, at, kickArc - 32, 6, 19.2, 31);

  // A site light tower on its trailer — dead, like half this expressway.
  const tow = at(kickArc - 16, 22.6);
  const towYaw = tow.dir + 0.5;
  kit.local(steel, tow, towYaw, 0, 0.34, 0, 1.5, 0.4, 2.6); // trailer bed
  kit.local(dark, tow, towYaw, -0.75, 0.28, 0.7, 0.22, 0.56, 0.56); // wheels
  kit.local(dark, tow, towYaw, 0.75, 0.28, 0.7, 0.22, 0.56, 0.56);
  kit.local(steel, tow, towYaw, 0, 0.85, -0.9, 0.9, 0.7, 0.8); // generator box
  kit.local(steel, tow, towYaw, 0, 3.2, 0, 0.22, 5.4, 0.22); // mast
  kit.local(steel, tow, towYaw, 0, 5.85, 0, 2.3, 0.14, 0.18); // head bar
  for (const ox of [-0.8, 0.8]) kit.local(dark, tow, towYaw, ox, 5.7, 0.06, 0.7, 0.42, 0.3);

  // Stacked kerb units + bundles of rebar, the way a Japanese site squares
  // its material away along the fence.
  for (let i = 0; i < 8; i++) {
    const p = at(kickArc - 27 + (i % 4) * 1.2, 20.6 + Math.floor(i / 4) * 0.85);
    kit.add(conc, p.x, p.y + 0.15 + Math.floor(i / 4) * 0.32, p.z, p.dir + (art(i * 7) - 0.5) * 0.28, 0.38, 0.32, 1.0);
  }
  for (let i = 0; i < 5; i++) {
    const p = at(kickArc - 21.5 + art(i * 5) * 1.2, 20.2 + art(i * 9) * 0.9);
    kit.add(rust, p.x, p.y + 0.16 + art(i * 3) * 0.3, p.z, p.dir + (art(i * 11) - 0.5) * 0.5, 0.3, 0.28, 3.6);
  }
  // Sandbags weighting every barricade foot (the Japanese roadworks tell).
  const bag = mat(0x6f6a5c, 0.98);
  for (let i = 0; i < 10; i++) {
    const p = at(kickArc - 12 + i * 2.1, 18.7 + art(i * 5) * 0.5);
    kit.add(bag, p.x, p.y + 0.13, p.z, p.dir + (art(i * 13) - 0.5) * 0.7, 0.66, 0.26, 0.4);
  }
  // A spoil heap where the deck was broken out.
  const heap = at(kickArc + 15, 20.4);
  const spoil = new THREE.Mesh(new THREE.ConeGeometry(2.2, 1.4, 7), mat(0x2f3138, 0.98));
  spoil.position.set(heap.x, heap.y + 0.6, heap.z);
  spoil.rotation.y = art(5) * Math.PI;
  g.add(spoil);
  kit.flush(g);
}

// ---- The viaduct: floor, parapets, box girder, T-piers, apron -------------

function buildViaduct(track: Track, sc: PlacedShortcut, g: THREE.Group): void {
  const ch = sc.channel;
  const P = ch.halfWidth; // the physics rail clamp
  const halfF = P + 0.35; // floor/parapet lateral (batch-1's visual wall line)
  const halfG = P + 0.1; // girder fascia inset — the floor overhangs it
  const nEnd = nodeAt(ch, ch.length - tuning.shortcuts.stubLen - 1);

  const asphMat = new THREE.MeshStandardMaterial({
    map: asphaltTexture(),
    roughness: 0.94,
    side: THREE.DoubleSide,
  });
  const parapetMat = new THREE.MeshStandardMaterial({
    map: concreteTexture('#5c5f6a', 8),
    roughness: 0.92,
    side: THREE.DoubleSide,
  });
  const girderMat = new THREE.MeshStandardMaterial({
    // Wider panel joints (64 px = 3 u): 1.5-u joints striped the whole span
    // from the road view (measured).
    map: concreteTexture('#383b44', 12, 64),
    roughness: 0.95,
    side: THREE.DoubleSide,
  });
  const steelMat = mat(0x262a30, 0.55);
  steelMat.side = THREE.DoubleSide;
  const pierMat = mat(0x474a54, 0.9);

  // Deck floor.
  g.add(
    chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * halfF, nd.y + 0.03, nd.z - nd.rightZ * halfF),
      new THREE.Vector3(nd.x + nd.rightX * halfF, nd.y + 0.03, nd.z + nd.rightZ * halfF),
    ], asphMat, 6),
  );

  // Box girder: fascia + soffit share ONE bottom line sampled at the spine —
  // clamped so the girder beds into the crest instead of digging in, and
  // opens to full depth where the deck flies.
  const girderBot = (nd: { x: number; y: number; z: number }): number =>
    Math.max(track.heightAt(nd.x, nd.z) - 0.35, nd.y - GIRDER_D);
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * halfG;
        const z = nd.z + nd.rightZ * s * halfG;
        return [new THREE.Vector3(x, girderBot(nd), z), new THREE.Vector3(x, nd.y - 0.04, z)];
      }, girderMat, 6),
    );
  }
  g.add(
    chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * halfG, girderBot(nd), nd.z - nd.rightZ * halfG),
      new THREE.Vector3(nd.x + nd.rightX * halfG, girderBot(nd), nd.z + nd.rightZ * halfG),
    ], girderMat, 6),
  );

  // Concrete parapets + steel pipe rail above them.
  for (const s of [-1, 1] as const) {
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * halfF;
        const z = nd.z + nd.rightZ * s * halfF;
        return [new THREE.Vector3(x, nd.y - 0.12, z), new THREE.Vector3(x, nd.y + 0.72, z)];
      }, parapetMat, 7),
    );
    g.add(
      chStrip(ch.nodes, ch.step, 0, nEnd, (nd) => {
        const x = nd.x + nd.rightX * s * (halfF + 0.06);
        const z = nd.z + nd.rightZ * s * (halfF + 0.06);
        return [new THREE.Vector3(x, nd.y + 1.02, z), new THREE.Vector3(x, nd.y + RAIL_TOP, z)];
      }, steelMat, 4),
    );
  }
  // The pink edge ribbon — mounted between parapet top and pipe rail so it
  // reads from the deck AND from the road below (the night skyline line).
  glowStrips(g, ch, 0, nEnd, halfF - 0.03, 0.78, PINK);

  // Pipe-rail posts (instanced — one draw call).
  const postMats: THREE.Matrix4[] = [];
  for (let i = 2; i < nEnd; i += 4) {
    const nd = ch.nodes[i];
    for (const s of [-1, 1] as const) {
      postMats.push(
        boxMatrix(
          nd.x + nd.rightX * s * (halfF + 0.06),
          nd.y + 0.93,
          nd.z + nd.rightZ * s * (halfF + 0.06),
          nd.dir,
          0.13,
          0.42,
          0.13,
        ),
      );
    }
  }
  instanced(g, steelMat, postMats);

  // Support piers: single-column T-piers (the Japanese urban-expressway
  // silhouette). HINT-LESS projection (M99): the spine runs to |lateral| 106
  // over 467 arc units — a hinted window clamps and misreads lateral exactly
  // where the cantilever skip below matters.
  const colMats: THREE.Matrix4[] = [];
  const capMats: THREE.Matrix4[] = [];
  for (let i = 3; i < nEnd - 2; i += 5) {
    const nd = ch.nodes[i];
    const proj = track.project(nd.x, nd.z);
    // deckStructure's cantilever contract: never a column on/near the road.
    if (Math.abs(proj.lateral) < track.halfWidth + 1.6) continue;
    const botY = nd.y - GIRDER_D;
    const colH = botY - proj.y;
    if (colH < 0.7) continue;
    if (colH >= 2.3) {
      capMats.push(boxMatrix(nd.x, botY - 0.42, nd.z, nd.dir, 5.5, 0.84, 1.6));
      const h = colH - 0.54;
      colMats.push(boxMatrix(nd.x, proj.y - 0.3 + h / 2, nd.z, nd.dir, 1.35, h, 1.35));
    } else {
      colMats.push(boxMatrix(nd.x, proj.y - 0.3 + (colH + 0.3) / 2, nd.z, nd.dir, 1.7, colH + 0.3, 1.2));
    }
  }
  instanced(g, pierMat, colMats);
  instanced(g, pierMat, capMats);

  // Landing apron: wider lip + end plate + a faint pink under-glow (the
  // batch-1 landing-invitation language, in Tokyo's colour).
  const n0 = ch.nodes[0];
  g.add(
    chStrip(ch.nodes, ch.step, 0, 2, (nd) => [
      new THREE.Vector3(nd.x - nd.rightX * (halfF + 0.7), nd.y + 0.05, nd.z - nd.rightZ * (halfF + 0.7)),
      new THREE.Vector3(nd.x + nd.rightX * (halfF + 0.7), nd.y + 0.05, nd.z + nd.rightZ * (halfF + 0.7)),
    ], parapetMat, 5),
  );
  g.add(box((halfF + 0.7) * 2, 0.7, 0.25, steelMat, n0.x, n0.y - 0.35, n0.z, n0.dir));
  // MEASURED: at 8×1.4 / 0.15 the under-glow bloomed into a solid magenta
  // slab hanging off the leading edge (the exposed sky behind it — NYC's
  // identical numbers hide among park trees). Smaller + fainter reads as a
  // glow, not a panel.
  const lipGlow = glowPlane((halfF + 0.2) * 2, 0.9, PINK, 0.09);
  lipGlow.position.set(n0.x, n0.y - 0.72, n0.z);
  lipGlow.rotation.y = n0.dir;
  g.add(lipGlow);
}

// ---- Deck life: gantries, sodium lamps, neon billboards, paint ------------

function buildGantry(ch: ChannelTrack, g: THREE.Group, d: number, lit: boolean): void {
  const i = nodeAt(ch, d);
  const nd = ch.nodes[i];
  const P = ch.halfWidth;
  const legLat = P + 0.56; // outside halfWidth + 0.5 — never flanking the lane
  const steel = mat(0x2b2e35, 0.6);
  const pos = (lat: number, y: number, fwd = 0): [number, number, number] => [
    nd.x + nd.rightX * lat + Math.sin(nd.dir) * fwd,
    nd.y + y,
    nd.z + nd.rightZ * lat + Math.cos(nd.dir) * fwd,
  ];
  for (const s of [-1, 1] as const) {
    // Mount bracket clamped over the parapet (outside the drive lane), then
    // the leg rising from it.
    const [bx, by, bz] = pos(s * (P + 0.32), 0.68);
    g.add(box(0.44, 0.3, 0.44, steel, bx, by, bz, nd.dir));
    const [lx, ly, lz] = pos(s * legLat, 4.48);
    g.add(box(0.3, 7.6, 0.3, steel, lx, ly, lz, nd.dir));
  }
  const [mx, my, mz] = pos(0, 8.3);
  g.add(box(legLat * 2 + 0.4, 0.3, 0.36, steel, mx, my, mz, nd.dir));
  // The board: bottom edge at +6.45 — the spanning-clearance rule (≥ 6.2
  // over the deck floor; the chase cam rides ~4.8 above the car, M116).
  const boardMat = lit
    ? new THREE.MeshBasicMaterial({ map: gantryBoardTexture(true) })
    : new THREE.MeshStandardMaterial({ map: gantryBoardTexture(false), roughness: 0.9 });
  const board = new THREE.Mesh(new THREE.PlaneGeometry(5.4, 1.7), boardMat);
  const [gx, gy, gz] = pos(0, 7.3, -0.06);
  board.position.set(gx, gy, gz);
  board.rotation.y = nd.dir + Math.PI; // faced to the APPROACH (M116c)
  g.add(board);
  const back = new THREE.Mesh(new THREE.PlaneGeometry(5.5, 1.8), steel);
  const [kx, ky, kz] = pos(0, 7.3, 0.06);
  back.position.set(kx, ky, kz);
  back.rotation.y = nd.dir;
  g.add(back);
  if (!lit) {
    // One live aircraft-warning beacon on the dead gantry.
    const beacon = new THREE.Mesh(
      new THREE.SphereGeometry(0.16, 8, 6),
      new THREE.MeshStandardMaterial({
        color: 0xffb347,
        emissive: new THREE.Color(AMBER),
        emissiveIntensity: 1.4,
        roughness: 0.4,
      }),
    );
    const [px, py, pz] = pos(0, 8.6);
    beacon.position.set(px, py, pz);
    g.add(beacon);
  }
}

function buildSodiumLamp(ch: ChannelTrack, g: THREE.Group, d: number, lit: boolean, sOut: -1 | 1): void {
  const i = nodeAt(ch, d);
  const nd = ch.nodes[i];
  const P = ch.halfWidth;
  const latP = sOut * (P + 0.6);
  const px = nd.x + nd.rightX * latP;
  const pz = nd.z + nd.rightZ * latP;
  const steel = mat(0x2c2f34, 0.65);
  g.add(box(0.16, 3.5, 0.16, steel, px, nd.y + 1.55, pz, nd.dir));
  // Arm reaches inward only to the parapet line — the head never hangs over
  // the drivable lane (the flanking-props rule, kept even overhead).
  const latA = sOut * (P + 0.38);
  g.add(box(0.5, 0.12, 0.14, steel, nd.x + nd.rightX * latA, nd.y + 3.24, nd.z + nd.rightZ * latA, nd.dir));
  const latH = sOut * (P + 0.16);
  const head = new THREE.Mesh(
    new THREE.BoxGeometry(0.36, 0.15, 0.22),
    lit
      ? new THREE.MeshStandardMaterial({
          color: 0xffc27a,
          emissive: new THREE.Color(0xff9a3c),
          emissiveIntensity: 1.6,
          roughness: 0.4,
        })
      : mat(0x24272c, 0.7),
  );
  head.position.set(nd.x + nd.rightX * latH, nd.y + 3.16, nd.z + nd.rightZ * latH);
  head.rotation.y = nd.dir;
  g.add(head);
}

function buildBillboard(ch: ChannelTrack, g: THREE.Group, d: number, chars: string, color: string, sOut: -1 | 1): void {
  const i = nodeAt(ch, d);
  const nd = ch.nodes[i];
  const P = ch.halfWidth;
  const steel = mat(0x23262c, 0.7);
  const latF = sOut * (P + 0.95);
  const fx = nd.x + nd.rightX * latF;
  const fz = nd.z + nd.rightZ * latF;
  // Frame hung on the OUTER fascia; mounts clamp back to the parapet. Sized
  // to read from the course (it is the "what's up there?" discovery hook),
  // and RAISED so the face clears the deck cross-section from a low road
  // angle (measured — at parapet height the near rail ate its lower half).
  g.add(box(0.18, 4.3, 2.6, steel, fx, nd.y + 2.55, fz, nd.dir));
  for (const my of [0.75, 2.9]) {
    const latM = sOut * (P + 0.62);
    g.add(box(0.6, 0.14, 0.14, steel, nd.x + nd.rightX * latM, nd.y + my, nd.z + nd.rightZ * latM, nd.dir));
  }
  // Lit face toward the ROAD (channel-inner side): normal = +right when the
  // deck hangs on the − side. Self-lit canvas = the neon reads at night.
  const face = new THREE.Mesh(
    new THREE.PlaneGeometry(2.4, 4.0),
    new THREE.MeshBasicMaterial({ map: neonSignTexture(chars, color) }),
  );
  const latIn = sOut * (P + 0.83);
  face.position.set(nd.x + nd.rightX * latIn, nd.y + 2.7, nd.z + nd.rightZ * latIn);
  face.rotation.y = nd.dir + (sOut < 0 ? Math.PI / 2 : -Math.PI / 2);
  g.add(face);
  const backLat = sOut * (P + 1.07);
  const back = new THREE.Mesh(new THREE.PlaneGeometry(2.5, 4.1), steel);
  back.position.set(nd.x + nd.rightX * backLat, nd.y + 2.7, nd.z + nd.rightZ * backLat);
  back.rotation.y = nd.dir + (sOut < 0 ? -Math.PI / 2 : Math.PI / 2);
  g.add(back);
}

/** A worn paint stencil lying on the deck, pitched to the local fall so it
 *  hugs the descending floor (flat decals clip on a 5.5% deck). */
function paintStencil(ch: ChannelTrack, g: THREE.Group, d: number, w: number, len: number, tex: THREE.CanvasTexture): void {
  const i = nodeAt(ch, d);
  const nd = ch.nodes[i];
  const decal = new THREE.Mesh(
    new THREE.PlaneGeometry(w, len),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.5, depthWrite: false }),
  );
  decal.position.set(nd.x, nd.y + 0.09, nd.z);
  decal.rotation.order = 'YXZ';
  decal.rotation.y = nd.dir;
  decal.rotation.x = -Math.PI / 2 - Math.atan(slopeAt(ch, i)); // M117 convention
  // MEASURED: without the in-plane flip the stencil reads UPSIDE-DOWN to the
  // driver (canvas-up lands pointing back up-course after the −π/2 lay-down).
  decal.rotation.z = Math.PI;
  g.add(decal);
}

function dressDeckLife(sc: PlacedShortcut, g: THREE.Group): void {
  const ch = sc.channel;
  const sOut = sc.door.side; // the bow pushes the deck toward the door side —
  // that channel-local side faces AWAY from the road (lamps/ads hang there).
  const driveLen = ch.length - tuning.shortcuts.stubLen;
  // One lit gantry right after the landing (it names where the spur drops
  // you); one dead gantry on the cantilever stretch beside the road.
  buildGantry(ch, g, driveLen * 0.17, true);
  buildGantry(ch, g, driveLen * 0.75, false);
  // Sodium lamps down the outer parapet — half the circuit is out.
  const lampsLit = [false, true, false, true];
  [0.13, 0.38, 0.63, 0.89].forEach((t, j) => buildSodiumLamp(ch, g, driveLen * t, lampsLit[j], sOut));
  // Two vertical neon boards facing the road below: one out in the money
  // stretch (a floating glow in the skyline), one on the near-road swing
  // (the "what's up there?" hook).
  buildBillboard(ch, g, driveLen * 0.56, 'ラーメン', PINK_CSS, sOut);
  buildBillboard(ch, g, driveLen * 0.78, 'カラオケ', CYAN_CSS, sOut);
  // Worn lane paint.
  const arrow = arrowStencilTexture();
  paintStencil(ch, g, driveLen * 0.09, 1.1, 3.2, arrow);
  paintStencil(ch, g, driveLen * 0.28, 1.4, 2.2, speedStencilTexture());
  paintStencil(ch, g, driveLen * 0.5, 1.1, 3.2, arrow);
}

// ---- The exit gap ---------------------------------------------------------

function buildExitWorks(sc: PlacedShortcut, g: THREE.Group): void {
  const conc = mat(0x565963, 0.9);
  dressExit(g, sc, conc);
  const d = sc.exitDoor;
  const courseDir = Math.atan2(d.dirX, d.dirZ);
  const tigMat = new THREE.MeshStandardMaterial({ map: tigerTexture(), roughness: 0.85, side: THREE.DoubleSide });
  const legMat = mat(0x2c2f34, 0.8);
  for (const s of [-1, 1] as const) {
    // Just beyond each jamb, tucked outside the wall line — a rival popping
    // out of a marked works gap reads deliberate.
    const bx = d.x + d.dirX * s * (d.half + 2.1) + d.rightX * d.side * 0.7;
    const bz = d.z + d.dirZ * s * (d.half + 2.1) + d.rightZ * d.side * 0.7;
    const yaw = courseDir + Math.PI / 2 + (art(s + 9) - 0.5) * 0.36;
    g.add(box(2.6, 0.5, 0.07, tigMat, bx, d.y + 0.78, bz, yaw));
    for (const t of [-1.05, 1.05]) {
      g.add(box(0.09, 1.04, 0.42, legMat, bx + d.dirX * t, d.y + 0.52, bz + d.dirZ * t, yaw));
    }
  }
  const lamp = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 8, 6),
    new THREE.MeshStandardMaterial({
      color: 0xffb347,
      emissive: new THREE.Color(AMBER),
      emissiveIntensity: 1.2,
      roughness: 0.4,
    }),
  );
  lamp.position.set(d.x + d.dirX * (d.half + 2.1) + d.rightX * d.side * 0.7, d.y + 1.12, d.z + d.dirZ * (d.half + 2.1) + d.rightZ * d.side * 0.7);
  g.add(lamp);
}

// ---- Entry ----------------------------------------------------------------

export function buildTokyoShortcut(track: Track, sc: PlacedShortcut, g: THREE.Group): void {
  buildRampWorks(track, sc, g);
  buildViaduct(track, sc, g);
  dressDeckLife(sc, g);
  buildExitWorks(sc, g);
}
